import { clsx } from "clsx";
import { type FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { TriggerButton } from "widgets";

import styleLayout from "../core/styles/PageLayout.module.scss";

import { getHomePageTips } from "../../app-utils/help.js";
import { filterByLicense } from "../../redux/modules/config.js";
import {
  availableDashboardCards,
  fetchDashboards,
  saveDashboard,
  setDashboardEditingMode,
  setDashboardFullscreenMode,
} from "../../redux/modules/dashboard.js";
import { type IAppState } from "../../redux/reducer.js";
import { type ReduxDispatch } from "../../redux/redux-utils.js";
import { GETTING_STARTED_ELEMENT_ID } from "../../settings/settings.js";
import TipTour from "../core/TipTour.js";
import PageHeader from "../core/navigation-panel/PageHeader.js";
import { type IGenericDashboardCard } from "./AddDashboardCardPanel.js";
import DashboardView, { type IDashboardConfig } from "./DashboardView.js";

const DashboardPage: FC = () => {
  const [availableCards, setAvailableCards] = useState<IGenericDashboardCard[]>([]);
  const configuration = useSelector((state: IAppState) => state.config.configuration);
  const dashboardState = useSelector((state: IAppState) => state.dashboardState);
  const dispatch = useDispatch<ReduxDispatch>();

  const getActiveDashboard = () => {
    return dashboardState.dashboards.find(
      (dashboard: IDashboardConfig) => dashboard.id === dashboardState.activeDashboardId,
    );
  };

  useEffect(() => {
    setAvailableCards(filterByLicense(availableDashboardCards, configuration));
    dispatch(fetchDashboards());
  }, []);

  return (
    <TipTour
      tips={getHomePageTips(configuration, getActiveDashboard())}
      disable={dashboardState.dashboardEditingMode || dashboardState.dashboardFullscreenMode}
    >
      <PageHeader
        title={"Dashboard"}
        subtitle={"Home"}
        setDocumentTitle
        actionWidgets={[
          <TriggerButton
            id={GETTING_STARTED_ELEMENT_ID.CUSTOMIZE_DASHBOARD}
            key={"dashboard-page-action-2"}
            isDisabled={dashboardState.dashboardFullscreenMode}
            icon={"dashboard_customize"}
            tooltip={"Customize the layout of the Dashboard"}
            onPress={() => {
              dispatch(setDashboardEditingMode(!dashboardState.dashboardEditingMode));
            }}
            label={"Customize"}
            showLabel
          />,
        ]}
      />

      <div className={clsx(styleLayout.page, styleLayout.page_scrollable, "panel-body--dashboard panel-home-page")}>
        {dashboardState.activeDashboardId && (
          <DashboardView
            editMode={dashboardState.dashboardEditingMode}
            toggleEditMode={() => dispatch(setDashboardEditingMode(!dashboardState.dashboardEditingMode))}
            saveDashboard={(dashboard: IDashboardConfig) => dispatch(saveDashboard(dashboard))}
            availableCards={availableCards}
            dashboard={getActiveDashboard()}
            onFullScreen={(fullscreenMode: boolean) => dispatch(setDashboardFullscreenMode(fullscreenMode))}
          />
        )}
      </div>
    </TipTour>
  );
};

export default DashboardPage;
